import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, SVGOverlay, Marker, Popup, TileLayer, useMap, useMapEvents, Pane, Polyline, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-ant-path';
import { trackEvent } from "../../analytics";

function MinZoomClamp({ minZoom }) {
  const map = useMap();

  useEffect(() => {
    if (typeof minZoom !== "number") return;
    map.setMinZoom(minZoom);

    if (map.getZoom() < minZoom) {
      map.setZoom(minZoom, { animate: false });
    }
  }, [map, minZoom]);

  useMapEvents({
    zoomend: () => {
      if (map.getZoom() < minZoom) {
        map.setZoom(minZoom, { animate: false });
      }
    },
  });

  return null;
}

function getStandLatLng({ map, centers, standId, zoomMax }) {
  const c = centers?.[standId];
  if (!map || !c) return null;

  const x = Number(c.x);
  const y = Number(c.y);
  if (Number.isNaN(x) || Number.isNaN(y)) return null;

  return map.unproject([x, y], zoomMax);
}

function flyToWithPixelOffset({ map, latlng, zoomTarget, offsetPx = [0, 0] }) {
  map.stop();
  
  const targetPoint = map.project(latlng, zoomTarget).add(offsetPx); 
  const targetLatLng = map.unproject(targetPoint, zoomTarget);
  
  map.flyTo(targetLatLng, zoomTarget, {
    animate: true,
    duration: 0.6,
    easeLinearity: 0.25,
  });
}

function focusStandWithOffset({
  map,
  centers,
  standId,
  zoomMax,
  zoomTarget = 3.5,
  offsetPx = [180, 0],
}) {
  const latlng = getStandLatLng({ map, centers, standId, zoomMax });
  if (!latlng) return false;

  flyToWithPixelOffset({ map, latlng, zoomTarget, offsetPx });
  return true;
}

function AutoFitRouteOnMobile({ rotaPositions = [], maxNativeZoom = 6, enabled = true }) {
  const map = useMap();
  const hasFittedRef = React.useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (!rotaPositions?.length) {
      hasFittedRef.current = false;
      return;
    }

    if (hasFittedRef.current) return;

    const latLngs = rotaPositions
      .filter((p) => Array.isArray(p) && p.length >= 2)
      .map(([legacyLat, legacyLng]) => {
        const x = Number(legacyLng);
        const y = Number(-legacyLat);
        return map.unproject([x, y], maxNativeZoom);
      });

    if (latLngs.length < 2) return;

    const bounds = L.latLngBounds(latLngs);
    
    map.stop();
  
    map.fitBounds(bounds, {
      paddingTopLeft: [24, 24],
      paddingBottomRight: [24, 220], 
      animate: true,
      duration: 0.35
    });

    const min = map.getMinZoom();
      setTimeout(() => {
        if (map.getZoom() < min) map.setZoom(min, { animate: false });
      }, 0);

    hasFittedRef.current = true;
  }, [rotaPositions, enabled, map, maxNativeZoom]);

  return null;
}

function AutoFitRouteOnTrigger({ rotaPositions = [], trigger = 0, maxNativeZoom = 6, enabled = true }) {
  const map = useMap();

  useEffect(() => {
    if (!enabled) return;
    if (!rotaPositions?.length || rotaPositions.length < 2) return;

    const latLngs = rotaPositions
      .filter((p) => Array.isArray(p) && p.length >= 2)
      .map(([legacyLat, legacyLng]) => {
        const x = Number(legacyLng);
        const y = Number(-legacyLat);
        return map.unproject([x, y], maxNativeZoom);
      });

    if (latLngs.length < 2) return;

    const bounds = L.latLngBounds(latLngs);

    const isMobile = window.innerWidth <= 960;

    map.stop();
  
    map.fitBounds(bounds, {
      paddingTopLeft: [24, 24],
      paddingBottomRight: isMobile ? [24, 220] : [24, 24],
      animate: true,
      duration: 0.35
    });

    const min = map.getMinZoom();
    setTimeout(() => {
      if (map.getZoom() < min) map.setZoom(min, { animate: false });
    }, 0);
    
  }, [trigger, rotaPositions, enabled, map, maxNativeZoom]);

  return null;
}

function darkenColor(color, percent) {
 if (!color) return '#808080';
 const num = parseInt(color.replace('#', ''), 16);
 const amt = Math.round(2.55 * percent);
 const R = (num >> 16) - amt;
 const G = ((num >> 8) & 0x00ff) - amt;
 const B = (num & 0x0000ff) - amt;
 return '#' + (0x1000000 + (R > 0 ? R : 0) * 0x10000 + (G > 0 ? G : 0) * 0x100 + (B > 0 ? B : 0)).toString(16).slice(1);
}

const normalizeText = (s) =>
  (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const getNodeTipoId = (node) => {
  const raw =
    node?.tipo_espaco_id ??
    node?.tipoEspacoId ??
    node?.tipo_id ??
    node?.tipo ??
    null;

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

function useDestinationIconFactory(tiposEspacoFeira = []) {
  const cacheRef = useRef(new Map());

  const tiposById = useMemo(() => {
    const map = new Map();
    (tiposEspacoFeira || []).forEach((t) => {
      map.set(String(t?.tipo_espaco_id), t);
    });
    return map;
  }, [tiposEspacoFeira]);

  const getIcon = (node) => {
    const tipoId = getNodeTipoId(node);
    if (tipoId === null) return null;

    const tipoConfig = tiposById.get(String(tipoId));
    if (!tipoConfig) return null;

    const corMarcador = tipoConfig?.cor || '#6b7280';
    const iconClass = tipoConfig?.icone || 'ri-map-pin-line';

    const cacheKey = `${tipoId}|${corMarcador}|${iconClass}`;
    if (cacheRef.current.has(cacheKey)) {
      return cacheRef.current.get(cacheKey);
    }

    const icon = L.divIcon({
      className: 'dest-marker-icon',
      html: `
        <div class="marker-wrapper-minimal" style="border-color: ${corMarcador}; background-color: white;">
          <i class="ri-computer-line" style="color: ${corMarcador};"></i>
        </div>
      `,
      iconSize: [26, 26],
      iconAnchor: [13, 13]
    });

    cacheRef.current.set(cacheKey, icon);
    return icon;
  };

  return { getIcon };
}

function useMapViewportState() {
  const map = useMap();
  const [state, setState] = useState(() => ({
    zoom: map.getZoom(),
    bounds: map.getBounds()
  }));

  useMapEvents({
    moveend: () => {
      setState({
        zoom: map.getZoom(),
        bounds: map.getBounds()
      });
    },
    zoomend: () => {
      setState({
        zoom: map.getZoom(),
        bounds: map.getBounds()
      });
    }
  });

  return state;
}

delete L.Icon.Default.prototype._getIconUrl;

const IMAGE_W = 19863;
const IMAGE_H = 14031;
const MAX_NATIVE_ZOOM = 7;

function DestinationsLayer({
  nodes = [],
  tiposEspacoFeira = [],
  filteredTypesIds = [],
  pesquisa = '',
  onPointSelected,
  maxNativeZoom = 6,
  minZoomToShow = 1, 
  highlightedNodeId = null, 
  configuracao
}) {
  const map = useMap();
  const { zoom, bounds } = useMapViewportState();
  const { getIcon } = useDestinationIconFactory(tiposEspacoFeira);

  const pxToLatLng = (x, y) => map.unproject([Number(x), Number(y)], maxNativeZoom);

  const q = normalizeText(pesquisa);
  const hasQuery = q.length > 0;
  const hasTypeFilter = Array.isArray(filteredTypesIds) && filteredTypesIds.length > 0;

  const visibleDestinations = useMemo(() => {
    if (zoom < minZoomToShow) return [];

    const list = (nodes || [])
      .filter((node) => node?.type === 'destination')
      .map((node) => ({ node, tipoId: getNodeTipoId(node) }))
      .filter(({ tipoId }) => tipoId !== null)
      .map(({ node, tipoId }) => {
        const latlng = pxToLatLng(node.x, node.y);

        const typeOk = !hasTypeFilter || filteredTypesIds.includes(tipoId);
        const nameOk = !hasQuery || normalizeText(node?.name).includes(q);

        return { node, tipoId, latlng, isMatch: typeOk && nameOk };
      })
      .filter(({ latlng }) => bounds.contains(latlng));

    // ✅ NOVO: se estiver filtrando por Serviço (tipo), renderiza só os matches
    if (hasTypeFilter) {
      return list.filter((d) => d.isMatch);
    }

    // (opcional) se quiser que pesquisa também esconda os demais:
    // if (hasQuery) return list.filter((d) => d.isMatch);

    return list;
  }, [nodes, filteredTypesIds, hasTypeFilter, hasQuery, q, bounds, zoom, minZoomToShow]);

  return (
    <>
      {visibleDestinations.map(({ node, tipoId, latlng, isMatch }) => {
        const icon = getIcon({ ...node, tipo_espaco_id: tipoId });
        if (!icon) return null;

        const isHighlighted = highlightedNodeId && node.id === highlightedNodeId;
        const shouldHighlightByFilter = (hasQuery || hasTypeFilter) && isMatch;

        return (
          <React.Fragment key={node.id}>
            {(isHighlighted || shouldHighlightByFilter) && (
              <CircleMarker
                center={latlng}
                radius={18}
                pathOptions={{
                  color: configuracao?.cor_estande_destacado,
                  weight: 8,
                  fillColor: configuracao?.cor_estande_destacado,
                  fillOpacity: 0.12,
                  className: 'leaflet-marker-pulsante',
                }}
              />
            )}

            <Marker position={latlng} icon={icon}>
              <Popup position={latlng}
                pane="popups"
                className="estande-popup-custom"
              >
                 <div style={{ textAlign: 'center' }}>
                   <strong>{node.name}</strong><br />
                   <button
                    onClick={() => {
                      trackEvent("route_create_click", {
                        feira_id: Number(node.feira_id) || 0,
                        node_id: node.id,
                        tipo_espaco_id: Number(node.tipo_espaco_id) || 0,
                        source: "marker_popup",
                      });

                      onPointSelected?.(node);
                    }}
                    style={{ cursor: 'pointer', marginTop: '5px' }}
                  >
                    Criar rota
                  </button>
                </div>
              </Popup>
            </Marker>
          </React.Fragment>
        );
      })}
    </>
  );
}

const AntPathWrapper = ({ positions, cor, maxNativeZoom = 6 }) => {
  const map = useMap();

  useEffect(() => {
    let antPath;
    if (!positions?.length) return;

    const converted = positions
      .filter((p) => Array.isArray(p) && p.length >= 2)
      .map(([legacyLat, legacyLng]) => {
        const x = Number(legacyLng);     // antigo lng = x
        const y = Number(-legacyLat);    // antigo lat = -y  -> volta para y positivo
        return map.unproject([x, y], maxNativeZoom);
      });

    if (converted.length > 1) {
      antPath = L.polyline.antPath(converted, {
        delay: 400,
        dashArray: [10, 20],
        weight: 10,
        color: cor || "rgba(11, 195, 97, 1)",
        pulseColor: "#FFF",
      });
      antPath.addTo(map);
    }

    return () => {
      if (antPath) map.removeLayer(antPath);
    };
  }, [positions, map, cor, maxNativeZoom]);

  return null;
};

function RasterTileSetup({ onBoundsReady, initialZoom = 1 }) {
  const map = useMap();

  useEffect(() => {
    const southWest = map.unproject([0, IMAGE_H], MAX_NATIVE_ZOOM);
    const northEast = map.unproject([IMAGE_W, 0], MAX_NATIVE_ZOOM);
    const bounds = L.latLngBounds(southWest, northEast);

    map.setMaxBounds(bounds.pad(0.05));
    onBoundsReady?.(bounds);

    // encaixa a imagem na viewport (sem “espaço” artificial)
    map.fitBounds(bounds, { animate: false });

    // se você quer forçar um zoom inicial específico (opcional):
    map.setZoom(initialZoom, { animate: false });
  }, [map, onBoundsReady, initialZoom]);

  return null;
}

const StandLabelsLayer = ({ stands, configuracao, centers }) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());

  useMapEvents({
    zoomend: () => setZoom(map.getZoom()),
  });

  if (zoom < -2) return null;

  const wrapText = (text, maxCharsPerLine) => {
    const clean = String(text || "").replace(/\s+/g, " ").trim();
    if (!clean) return [];
    const words = clean.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      // Se a palavra sozinha for maior que o limite, ela entra sozinha na linha
      if (word.length > maxCharsPerLine && !currentLine) {
        lines.push(word);
        return;
      }

      if ((currentLine + (currentLine ? " " : "") + word).length <= maxCharsPerLine) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const getLabelXY = (stand) => {
    const x = stand?.label_pos_x ?? stand?.posicao_x ?? centers?.[stand.id]?.x ?? null;
    const y = stand?.label_pos_y ?? stand?.posicao_y ?? centers?.[stand.id]?.y ?? null;
    if (x === null || y === null) return null;
    return { x: Number(x), y: Number(y) };
  };

  const COR_TITULO = configuracao?.cor_label || "#000";
  const COR_EXPOSITOR = configuracao?.cor_label_expositor || COR_TITULO;

  return (
    <g id="labels-expositores" style={{ pointerEvents: 'none', userSelect: 'none' }}>
      {stands.map((stand) => {
        const pos = getLabelXY(stand);
        const box = centers?.[stand.id];
        if (!pos || !box) return null;

        const nomeEspaco = (stand?.nome || stand?.numero || '').toString().trim();
        const nomeExpo = (stand.expositorNome || "").toString().trim();
        if (!nomeEspaco && !nomeExpo) return null;

        const baseBoxW = Number(box.w || 120);
        const baseBoxH = Number(box.h || 80);

        // --- LÓGICA DE DIMENSIONAMENTO REFINADA ---
        
        // 1. Calculamos um fator de escala baseado na largura. 
        // Se o estande for menor que 60px, a fonte reduz drasticamente.
        const scaleFactor = baseBoxW < 60 ? 0.15 : 0.11;
        
        // 2. Definimos tamanhos mínimos e máximos para a fonte não sumir nem ficar gigante
        let fontSizeExpo = Math.max(6, Math.min(baseBoxW * scaleFactor, 12));
        const fontSizeEspaco = Math.max(7, Math.min(baseBoxW * 0.13, 13));

        // 3. Forçamos a quebra de linha: quanto menor o estande, menos caracteres por linha.
        // Reduzimos o divisor de 0.6 para 0.5 para ser mais rigoroso na quebra.
        const approxCharsPerLine = Math.max(5, Math.floor((baseBoxW * 0.85) / (fontSizeExpo * 0.55)));
        
        const lines = [];
        if (nomeEspaco) {
          lines.push({ text: nomeEspaco.toUpperCase(), size: fontSizeEspaco, weight: 800, fill: COR_TITULO });
        }
        
        const expoLines = wrapText(nomeExpo.toUpperCase(), approxCharsPerLine);
        
        // 4. Ajuste de emergência: se gerou muitas linhas e vai estourar a altura, 
        // diminuímos a fonte do expositor um pouco mais.
        const tempLineSpacing = fontSizeExpo * 1.1;
        if ((expoLines.length + 1) * tempLineSpacing > baseBoxH) {
           fontSizeExpo *= 0.85;
        }

        const lineSpacing = fontSizeExpo * 1.15;
        expoLines.forEach(t => {
          lines.push({ text: t, size: fontSizeExpo, weight: 500, fill: COR_EXPOSITOR });
        });

        const totalBlockHeight = (lines.length - 1) * lineSpacing;
        const startY = pos.y - (totalBlockHeight / 2);

        // Oculta apenas se realmente não houver espaço físico (ajuste fino no zoom)
        if (baseBoxW < 20 || (totalBlockHeight > baseBoxH * 1.5 && zoom < 1)) return null;

        return (
          <g key={`lbl-${stand.id}`}>
            {lines.map((l, idx) => (
              <text
                key={`lbl-${stand.id}-${idx}`}
                x={pos.x}
                y={startY + (idx * lineSpacing)}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={l.fill}
                style={{ 
                  fontSize: `${l.size}px`, 
                  fontWeight: l.weight, 
                  fontFamily: 'sans-serif'
                }}
              >
                {l.text}
              </text>
            ))}
          </g>
        );
      })}
    </g>
  );
};

const MapaFeiraCompleto = ({
  feiraId,
  configuracao,
  estandesCadastrados,
  tiposEspacoFeira = [],
  tilesUrl,
  onEstandeClick,
  nodes = [],
  mapData,
  estandeDestacado,
  rotaPositions,
  filteredEstandesIds,
  onPointSelected,
  filteredTypesIds,
  pesquisa,
  handleCriarRota,
  highlightedNodeId,
  routeZoomTrigger,
  estandeDestacadoOrigem,
  estandeDestacadoDestino,
  destacarEstandeRef
}) => {
  const mapRef = useRef(null);
  const [selectedStandPopup, setSelectedStandPopup] = useState(null);
  const [rasterBounds, setRasterBounds] = useState(null);
  const isMobile = window.innerWidth <= 768; 
  const lastFocusRef = useRef({ id: null, t: 0 });
  const lastAutoOpenRef = useRef({ id: null, t: 0 });
  const prevHasAnyFilterRef = useRef(false);

  const getInitialZoomByScreen = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;

    const isTotem =
      w >= 1600 &&
      h >= 900 &&
      (window.matchMedia?.("(pointer: coarse)")?.matches ?? false);

    if (isTotem) return 3;

    // Celular
    if (w <= 768) return 1.5;

    // Notebook / telas médias (ex.: MacBook)
    if (w <= 1366) return 2.5;   // aqui você já disse que 1 ficou melhor

    // Desktop normal
    return 3; // ou 3 se você preferir
  };

  const [initialZoom, setInitialZoom] = useState(getInitialZoomByScreen);

  useEffect(() => {
    const onResize = () => setInitialZoom(getInitialZoomByScreen());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const getExpositorNome = (estande) => {
    const exp = Array.isArray(estande?.expositor) ? estande.expositor[0] : estande?.expositor;
    if (!exp) return "";
    const raw = (exp.nome_mapa && String(exp.nome_mapa).trim())
      ? exp.nome_mapa
      : (exp.nome || exp.nome_fantasia || "");
    return String(raw).trim();
  };

  const processedStandsBase = useMemo(() => {
    if (!mapData?.paths || !Array.isArray(estandesCadastrados)) return [];

    const standsByHash = new Map(
      estandesCadastrados.map((e) => [String(e.hash_path), e])
    );

    const tiposById = new Map(
      (tiposEspacoFeira || []).map((t) => [Number(t.tipo_espaco_id), t])
    );

    const corPadrao = configuracao?.cor_estande_padrao || '#8f4de9';

    return Object.values(mapData.paths)
      .map((pathData) => {
        const stand = standsByHash.get(String(pathData.hash));
        if (!stand) return null;

        const tipo = tiposById.get(Number(stand.tipo_espaco_id));
        const corBase = tipo?.cor || corPadrao;

        return {
          ...stand,
          path: pathData.path,
          corBase,
          expositorNome: getExpositorNome(stand),
        };
      })
      .filter(Boolean);
  }, [
    mapData,
    estandesCadastrados,
    tiposEspacoFeira,
    configuracao?.cor_estande_padrao,
  ]);

  const processedStands = useMemo(() => {
    if (!processedStandsBase?.length) return [];

    const filteredIdsSet = Array.isArray(filteredEstandesIds)
      ? new Set(filteredEstandesIds.map(Number))
      : null;

    const corContorno = configuracao?.cor_contorno_estande || '#999';
    const corDestaque = configuracao?.cor_estande_destacado || '#FFD700';

    return processedStandsBase.map((stand) => {
      const standId = Number(stand.id);

      const isDestacado = !!(
        estandeDestacado &&
        Number(estandeDestacado.id) === standId
      );

      const isMatch = filteredIdsSet ? filteredIdsSet.has(standId) : false;
      const hasStandFilter = !!filteredIdsSet;

      let svgStyle = {
        fill: stand.expositor ? stand.corBase : '#ffffff',
        stroke: corContorno,
        strokeWidth: 1,
        opacity: 1,
      };

      if (isDestacado || isMatch) {
        svgStyle = {
          fill: corDestaque,
          stroke: darkenColor(corDestaque, 40),
          strokeWidth: 3,
          opacity: 1,
        };
      }

      const isOrigem = !!(estandeDestacadoOrigem && Number(estandeDestacadoOrigem.id) === standId);
      const isDestino = !!(estandeDestacadoDestino && Number(estandeDestacadoDestino.id) === standId);

      if (isDestino || isOrigem) {
        const cor = corDestaque;
        svgStyle = { fill: cor, stroke: darkenColor(cor, 40), strokeWidth: 3, opacity: 1 };
      } else if (hasStandFilter && isMatch) {
        svgStyle = {
          ...svgStyle,
          stroke: corDestaque,
          strokeWidth: 3,
          opacity: 1,
        };
      }

      return {
        ...stand,
        svgStyle,
        isDestacado,
        isMatch,
        corFill: svgStyle.fill,
      };
    });
  },[
    processedStandsBase,
    estandeDestacado,
    filteredEstandesIds,
    configuracao?.cor_contorno_estande,
    configuracao?.cor_estande_destacado,
    estandeDestacadoOrigem,
    estandeDestacadoDestino
  ]);

  const centers = useMemo(() => {
  const next = {};

  (processedStandsBase || []).forEach((stand) => {
    const x = stand?.posicao_x ?? stand?.center_x ?? null;
    const y = stand?.posicao_y ?? stand?.center_y ?? null;

    if (x === null || y === null) return;

    next[stand.id] = {
      x: Number(x),
      y: Number(y),
      w: Number(stand?.largura ?? stand?.w ?? 120),
      h: Number(stand?.altura ?? stand?.h ?? 80),
    };
  });

  return next;
}, [processedStandsBase]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!estandeDestacado?.id) return;
    if (rotaPositions?.length > 1) return;

    const now = Date.now();
    if (lastFocusRef.current.id === estandeDestacado.id && (now - lastFocusRef.current.t) < 500) {
      return;
    }
    lastFocusRef.current = { id: estandeDestacado.id, t: now };
    
    focusStandWithOffset({
      map,
      centers,
      standId: estandeDestacado.id,
      zoomMax: MAX_NATIVE_ZOOM,
      zoomTarget: 3.5,
      offsetPx: [220, 0], 
    });
  }, [centers, estandeDestacado?.id, rotaPositions]);

  const abrirPopupEstande = useCallback((stand) => {
    if (!stand || !mapRef.current) return;
    
    const map = mapRef.current;
    const pos = centers[stand.id];
    if (pos) {
      const latlng = map.unproject([pos.x, pos.y], MAX_NATIVE_ZOOM);
      setSelectedStandPopup({
        ...stand,
        latlng: latlng,
        expositorNome: getExpositorNome(stand)
      });
    }
  }, [centers]);

  useEffect(() => {
    if (destacarEstandeRef) {
      destacarEstandeRef.current = (stand) => {
        abrirPopupEstande(stand);
      };
    }
  }, [destacarEstandeRef, abrirPopupEstande]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!filteredEstandesIds || filteredEstandesIds.length !== 1) return;
    if (rotaPositions?.length > 1) return;
    const standId = filteredEstandesIds[0];

    focusStandWithOffset({
      map,
      centers,
      standId: filteredEstandesIds[0],
      zoomMax: MAX_NATIVE_ZOOM,
      zoomTarget: 3.5,
      offsetPx: [220, 0], 
    });

      const now = Date.now();
      if (lastAutoOpenRef.current.id === standId && (now - lastAutoOpenRef.current.t) < 600) return;
      lastAutoOpenRef.current = { id: standId, t: now };
      
      const stand = (processedStandsBase || []).find((s) => Number(s.id) === standId);
      if (stand) {
        abrirPopupEstande(stand);
      }

  }, [centers, filteredEstandesIds, rotaPositions, processedStandsBase, abrirPopupEstande]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!rasterBounds) return;

    const hasServiceFilter = Array.isArray(filteredTypesIds) && filteredTypesIds.length > 0;

    if (!hasServiceFilter) return;

    if (rotaPositions?.length > 1) return;

    map.stop();

    map.fitBounds(rasterBounds, {
      paddingTopLeft: [24, 24],
      paddingBottomRight: [24, 24],
      animate: true,
      duration: 0.35,
    });

    setTimeout(() => {
      map.setZoom(initialZoom, { animate: false });
    }, 0);
  }, [filteredTypesIds, rasterBounds, initialZoom, rotaPositions]);

  useEffect(() => {
    const hasAnyFilter = filteredEstandesIds !== null;

    if (prevHasAnyFilterRef.current && !hasAnyFilter) {
      setSelectedStandPopup(null);
    }

    prevHasAnyFilterRef.current = hasAnyFilter;
  }, [filteredEstandesIds]);

  if (!feiraId || !mapData || !tilesUrl) return null;

  return (
    <MapContainer
      ref={mapRef}
      crs={L.CRS.Simple}
      center={[0, 0]}
      zoom={initialZoom}
      minZoom={initialZoom}
      zoomAnimation={false} 
      fadeAnimation={false}
      markerZoomAnimation={true}
      updateWhenIdle={false}      
      updateWhenZooming={false}
      zoomSnap={0.5} 
      zoomDelta={0.5}
      preferCanvas={true}
      
      bounceAtZoomLimits={false}
      maxZoom={MAX_NATIVE_ZOOM}
      style={{ height: '100%', width: '100%' }}
      
      maxBounds={rasterBounds ?? undefined}
      maxBoundsViscosity={1.0}
    >
      <RasterTileSetup onBoundsReady={setRasterBounds} initialZoom={initialZoom}/>
      <MinZoomClamp minZoom={initialZoom} />
      <Pane name="popups" style={{ zIndex: 9999 }} />

      <Pane name="tiles" style={{ zIndex: 200 }}>
        <TileLayer
          url={tilesUrl}
          keepBuffer={12}             
          updateInterval={100}       
          opacity={1}
          tileSize={256}
          maxNativeZoom={MAX_NATIVE_ZOOM}
          noWrap={true}
          bounds={rasterBounds}
          detectRetina={false}
        />
      </Pane>

      <Pane name="stands" style={{ zIndex: 400 }}>
        <SVGOverlay
          bounds={rasterBounds}
          attributes={{
            viewBox: `0 0 ${IMAGE_W} ${IMAGE_H}`,
            'pointer-events': 'none'
          }}
        >
          <g id="camada-estandes">
            {processedStands.map((stand) => (
              <path
                key={`path-${stand.id}`}
                d={stand.path}
                fill={stand.corFill}
                stroke="#666"
                strokeWidth="1"
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                onClick={(e) => {
                  L.DomEvent.stopPropagation(e);

                  const map = mapRef.current; 

                  if (stand.expositor) {
                    if (map) {
                      const latlng = map.mouseEventToLatLng(e.originalEvent || e);

                      setSelectedStandPopup({
                        ...stand,
                        latlng: latlng
                      });
                    }
                    onEstandeClick?.(stand);
                  }
                }}
              />
            ))}

            <StandLabelsLayer 
              stands={processedStands} 
              configuracao={configuracao} 
              centers={centers} 
            />
          </g>
        </SVGOverlay>
      </Pane>

      <Pane name="routes" style={{ zIndex: 500 }}>
        {rotaPositions?.length > 0 && (
          <AntPathWrapper
            positions={rotaPositions}
            cor={configuracao?.cor_traco_rota}
            maxNativeZoom={MAX_NATIVE_ZOOM}
          />
        )}
      </Pane>

      <Pane name="markers" style={{ zIndex: 600 }}>
        <DestinationsLayer
          nodes={nodes}
          tiposEspacoFeira={tiposEspacoFeira}
          filteredTypesIds={filteredTypesIds}
          pesquisa={pesquisa}
          onPointSelected={onPointSelected}
          maxNativeZoom={MAX_NATIVE_ZOOM}
          highlightedNodeId={highlightedNodeId}
          configuracao={configuracao}
        />
      </Pane>

      <AutoFitRouteOnMobile
        rotaPositions={rotaPositions}
        maxNativeZoom={MAX_NATIVE_ZOOM}
        enabled={isMobile}
      />

      <AutoFitRouteOnTrigger
        rotaPositions={rotaPositions}
        trigger={routeZoomTrigger}
        maxNativeZoom={MAX_NATIVE_ZOOM}
        enabled={!isMobile && !!rotaPositions?.length}
      />
      {selectedStandPopup && (
        <Popup
          position={selectedStandPopup.latlng}
          onClose={() => setSelectedStandPopup(null)}
          pane="popups"
          className="estande-popup-custom"
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '4px' }}>
            {selectedStandPopup.expositor.logoUrl && (
              <img 
                src={selectedStandPopup.expositor.logoUrl} 
                alt={selectedStandPopup.expositorNome} 
                style={{ width: '60px', height: 'auto', borderRadius: '4px' }} 
              />
            )}
            <div style={{ textAlign: 'center' }}>
              <strong style={{ display: 'block', fontSize: '14px' }}>
                  {selectedStandPopup.expositorNome || 'Expositor não informado'}
              </strong>
              <span style={{ fontSize: '12px', color: '#666' }}>
                  {selectedStandPopup.nome || ''}
              </span>
            </div>

            <button
              onClick={() => {
                trackEvent("route_create_click", {
                  feira_id: Number(selectedStandPopup.feira_id) || 0,
                  expositor_id: selectedStandPopup?.expositor?.id || 0,
                  stand_id: selectedStandPopup?.id || 0,
                  source: "stand_popup",
                });
                
                handleCriarRota(selectedStandPopup.expositor);
                
                setSelectedStandPopup(null);
              }}
              style={{
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: configuracao?.cor_primaria || '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
                width: '100%'
              }}
            >
              Criar Rota
            </button>
          </div>
        </Popup>
      )}

   
    </MapContainer>
  );
};

export default React.memo(MapaFeiraCompleto, (prev, next) => {
  return (
    prev.estandeDestacado?.id === next.estandeDestacado?.id &&
    prev.filteredEstandesIds === next.filteredEstandesIds &&
    prev.rotaPositions?.length === next.rotaPositions?.length &&
    prev.pesquisa === next.pesquisa &&
    prev.zoomTrigger === next.zoomTrigger
  );
});